import { FileText, MessagesSquare, Scale } from "lucide-react";
import type { RagNavConfig } from "@/apps/rag/rag-nav.js";
import { BACK_TO_GLOBAL_CHAT_LABEL, WORKSPACE_GLOBAL_CHAT_LABEL } from "@/apps/rag/constants.js";
import { APPS_HOME_PATH } from "@/lib/apps";
import {
  publicLibraryChatsPath,
  publicLibraryDocumentsPath,
  regulatoryGlobalChatsPath,
  regulatoryGlobalChatPath,
  regulatoryLibrariesPath,
  REGULATORY_BASE,
} from "./paths.js";

function regulatoryRootNavItems() {
  return [
    {
      to: regulatoryGlobalChatsPath(),
      label: WORKSPACE_GLOBAL_CHAT_LABEL,
      icon: MessagesSquare,
    },
    {
      to: regulatoryLibrariesPath(),
      label: "Regulatory Libraries",
      icon: Scale,
      end: true,
    },
  ];
}

export function buildRegulatoryRootNav(): RagNavConfig {
  return {
    sectionLabel: "Regulatory",
    items: regulatoryRootNavItems(),
  };
}

export function buildRegulatoryGlobalChatNav(
  recentChats: { id: string; title: string }[] = [],
): RagNavConfig {
  return {
    sectionLabel: "Regulatory",
    items: regulatoryRootNavItems(),
    subSectionLabel: recentChats.length ? "Recent" : undefined,
    subItems: recentChats.slice(0, 5).map((chat) => ({
      id: chat.id,
      to: regulatoryGlobalChatPath(chat.id),
      label: chat.title,
    })),
    footerAction: {
      to: regulatoryGlobalChatsPath(),
      label: "New chat",
      createChat: { scope: "regulatoryGlobal" },
    },
  };
}

export function buildPublicLibraryNav(libraryId: string, libraryName: string): RagNavConfig {
  return {
    sectionLabel: libraryName,
    items: [
      {
        to: publicLibraryDocumentsPath(libraryId),
        label: "Documents",
        icon: FileText,
      },
    ],
  };
}

export interface RegulatoryBackLink {
  to: string;
  label: string;
}

export function resolveRegulatoryBackLink(pathname: string): RegulatoryBackLink {
  if (pathname === REGULATORY_BASE || pathname === `${REGULATORY_BASE}/`) {
    return { to: APPS_HOME_PATH, label: "Back to apps" };
  }

  const globalChatThreadMatch = pathname.match(/^\/regulatory\/chat\/([^/]+)/);
  if (globalChatThreadMatch) {
    return { to: regulatoryGlobalChatsPath(), label: BACK_TO_GLOBAL_CHAT_LABEL };
  }

  if (pathname === regulatoryGlobalChatsPath() || pathname === `${regulatoryGlobalChatsPath()}/`) {
    return { to: APPS_HOME_PATH, label: "Back to apps" };
  }

  const librariesPath = regulatoryLibrariesPath();
  if (pathname === librariesPath || pathname === `${librariesPath}/`) {
    return { to: APPS_HOME_PATH, label: "Back to apps" };
  }

  const libraryChatMatch = pathname.match(/^\/regulatory\/libraries\/([^/]+)\/chats\/([^/]+)/);
  if (libraryChatMatch) {
    return {
      to: publicLibraryChatsPath(libraryChatMatch[1]),
      label: "Back to chats",
    };
  }

  const libraryChatsMatch = pathname.match(/^\/regulatory\/libraries\/([^/]+)\/chats\/?$/);
  if (libraryChatsMatch) {
    return { to: regulatoryLibrariesPath(), label: "Back to regulatory libraries" };
  }

  const libraryDocumentsMatch = pathname.match(/^\/regulatory\/libraries\/([^/]+)\/documents/);
  if (libraryDocumentsMatch) {
    return { to: regulatoryLibrariesPath(), label: "Back to regulatory libraries" };
  }

  return { to: regulatoryLibrariesPath(), label: "Back to regulatory libraries" };
}
